import { useState } from 'react';
import { IconLibrary } from '../../builder/editors/IconLibrary';
import { Picto } from '../ui/PictoIcon';
import { isPictoName } from '../ui/picto';

/**
 * PictoPicker — le choix du picto d'une entité, en React pur.
 *
 * Le picto est une ICÔNE SVG (Lucide) : le champ stocke le NOM de l'icône et le
 * choix se fait dans `IconLibrary`, la pop-in plein écran du builder — le set
 * complet (~1700), rangé par thèmes et cherchable en français. Une grille d'une
 * quinzaine d'emoji ne couvrait ni les métiers ni les marques, et son rendu
 * variait d'un système à l'autre.
 *
 * La valeur héritée (un emoji) reste affichée telle quelle par `Picto` tant
 * qu'aucune icône n'a été choisie : rien à migrer pour que l'existant reste
 * lisible.
 *
 * Deux enveloppes autour de ce composant, une par monde :
 *   • `PictoPickerField` — le champ reactive-json (`dataLocation`), utilisé par
 *     le formulaire de projet ;
 *   • l'appel direct, dans un formulaire React (les formulaires du wiki).
 *
 * Props :
 *   value    – le nom Lucide stocké (ou un emoji hérité, ou '')
 *   onChange – (name) => void
 *   label    – intitulé rappelé dans le libellé accessible du bouton
 *   readOnly – désactive l'ouverture de la bibliothèque (défaut : false)
 */
export const PictoPicker = ({ value, onChange, label = 'le picto', readOnly = false }) => {
    const [open, setOpen] = useState(false);
    const selected = typeof value === 'string' ? value : '';

    const handlePick = (name) => {
        if (readOnly) return;
        onChange?.(name);
    };

    return (
        <>
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    disabled={readOnly}
                    aria-label={`Choisir ${label}${selected ? ` (actuellement : ${selected})` : ''}`}
                    className="picto-trigger"
                >
                    <Picto name={selected} size={24} />
                </button>
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    disabled={readOnly}
                    className="btn btn-ghost btn-sm"
                >
                    {selected ? 'Changer l’icône' : 'Choisir une icône'}
                </button>
            </div>

            {open && (
                <IconLibrary
                    value={isPictoName(selected) ? selected : null}
                    onPick={handlePick}
                    onClose={() => setOpen(false)}
                />
            )}
        </>
    );
};

export default PictoPicker;
